import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import backofficeApiClient, {
  CustomerResponseModel,
  CreateCustomerRequest,
  CustomerStatusFilterEnum,
  CustomerInfoResponseModel,
  CustomerAdditionalInfoReplyRequestModel,
  CustomerHideIndicatorModel,
  FileTypeEnum,
} from 'api/backofficeApiClient';
import adminApiClient, {
  CustomerApproveStepModel,
  CustomerAdditionalInfoModel,
  CustomerDocumentTypeEnum,
  CustomerCompaniesFilterEnum,
  CustomerGroup,
  CreateCustomerGroupViewModel,
} from 'api/adminApiClient';
import { logout } from './authenticationSlice';
import { isAnyActionOf } from 'redux/utils';

interface ClientState {
  isClientModalOpen: boolean;
  approvedClients: CustomerResponseModel[];
  clientCreatedAt?: number;
  clientDraftCreatedAt?: number;
  clientDetails?: CustomerInfoResponseModel;
  customerDetailsUpdatedAtByAdmin?: number;
  customerGroups: CustomerGroup[];
}

const initialState: ClientState = {
  isClientModalOpen: false,
  approvedClients: [],
  clientCreatedAt: undefined,
  clientDraftCreatedAt: undefined,
  clientDetails: undefined,
  customerDetailsUpdatedAtByAdmin: undefined,
  customerGroups: [],
};

const createNewClient = createAsyncThunk(
  'client/createNewClient',
  async (data: CreateCustomerRequest) => {
    return backofficeApiClient.createNewCustomer(data);
  },
);

const createDraftClient = createAsyncThunk(
  'client/createDraftClient',
  async (data: CreateCustomerRequest) => {
    return backofficeApiClient.createNewDraftCustomer(data);
  },
);

interface UpdateDraftClient {
  customerId: string;
  data: CreateCustomerRequest;
}

const updateDraftClient = createAsyncThunk(
  'client/updateDraftClient',
  async ({ customerId, data }: UpdateDraftClient) => {
    return backofficeApiClient.updateCustomerDraft(customerId, data);
  },
);

interface CrateClientFromDraft {
  customerId: string;
  data: CreateCustomerRequest;
}

const crateClientFromDraft = createAsyncThunk(
  'client/crateClientFromDraft',
  async ({ customerId, data }: CrateClientFromDraft) => {
    return backofficeApiClient.migrateCustomerDraft(customerId, data);
  },
);

interface UpdateClient {
  isAdmin: string;
  customerId: string;
  data: CreateCustomerRequest;
}

const updateClient = createAsyncThunk(
  'client/updateClient',
  async ({ customerId, data, isAdmin }: UpdateClient) => {
    return isAdmin
      ? adminApiClient.updateCustomerInfo(customerId, data)
      : backofficeApiClient.updateCustomerInfo(customerId, data);
  },
);

interface UpdateClientAdditionalInfo {
  customerId: string;
  data: CustomerAdditionalInfoModel;
}

const updateClientAdditionalInfo = createAsyncThunk(
  'client/updateClientAdditionalInfo',
  async ({ customerId, data }: UpdateClientAdditionalInfo) => {
    return adminApiClient.updateCustomerAdditionalInfo(customerId, data);
  },
);

interface ClientListModel {
  pageNumber: number;
  requestId: number;
  isAdminRole?: boolean;
  companyIds?: string[];
  searchQuery?: string;
  statusFilter: CustomerStatusFilterEnum;
}
const getClientRequests = createAsyncThunk(
  'client/getClientRequests',
  async ({
    pageNumber,
    searchQuery,
    statusFilter,
    companyIds,
    isAdminRole,
  }: ClientListModel) => {
    return isAdminRole
      ? adminApiClient.getCustomersPagedList(
          pageNumber,
          statusFilter,
          searchQuery,
          companyIds,
        )
      : backofficeApiClient.getCustomersPagedList(
          pageNumber,
          statusFilter,
          searchQuery,
          companyIds,
        );
  },
);

const getCustomerListExcel = createAsyncThunk(
  'client/getCustomerListExcel',
  async () => {
    const result = await adminApiClient.exportCustomers();
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

interface ClientDetailsModel {
  clientKey: string;
  isAdminRole?: boolean;
}

const getClientInfoByKey = createAsyncThunk(
  'client/getClientInfoByKey',
  async ({ clientKey, isAdminRole }: ClientDetailsModel) => {
    return isAdminRole
      ? adminApiClient.getCustomerInfoByKey(clientKey)
      : backofficeApiClient.getCustomerInfoByKey(clientKey);
  },
);

const getClientInfo = createAsyncThunk(
  'client/getClientInfo',
  async ({ clientKey, isAdminRole }: ClientDetailsModel) => {
    return isAdminRole
      ? adminApiClient.getCustomerInfo(clientKey)
      : backofficeApiClient.getCustomerInfo(clientKey);
  },
);

const getMyApprovedClients = createAsyncThunk(
  'client/getMyApprovedClients',
  async () => {
    return await backofficeApiClient.getMyCustomerList();
  },
);

const getApprovedClientsByMember = createAsyncThunk(
  'client/getApprovedClientsByMember',
  async (companyId: string) => {
    return await adminApiClient.getCustomerListForMember(companyId);
  },
);

const getMyApprovedClientInfo = createAsyncThunk(
  'client/getMyApprovedClientInfo',
  async (clientId: string) => {
    return await backofficeApiClient.getCustomerInfo(clientId);
  },
);

interface GetApprovedClientsCompaniesParams {
  customerCompaniesFilter: CustomerCompaniesFilterEnum;
}
const getApprovedClientsCompanies = createAsyncThunk(
  'client/getApprovedClientsCompanies',
  async ({ customerCompaniesFilter }: GetApprovedClientsCompaniesParams) => {
    return await adminApiClient.getCompaniesWithApprovedMembersOrCustomer(
      customerCompaniesFilter,
    );
  },
);

const getAllCustomersCompanies = createAsyncThunk(
  'client/getAllCustomersCompanies',
  async () => {
    return await adminApiClient.getAllCustomersCompanies();
  },
);

const completeClientProgressStep = createAsyncThunk(
  'client/completeClientProgressStep',
  async (stepData: CustomerApproveStepModel) => {
    await adminApiClient.customerApproveStep(stepData);

    return adminApiClient.getCustomerInfo(stepData.customerId as string);
  },
);

const sendCustomerAdditionalInfoResponse = createAsyncThunk(
  'client/sendCustomerAdditionalInfoResponse',
  async (responseInfo: CustomerAdditionalInfoReplyRequestModel) => {
    const { customerId } = responseInfo;

    await backofficeApiClient.sendCustomerAdditionalInfoReply(responseInfo);

    return backofficeApiClient.getCustomerInfo(customerId);
  },
);

const hideCustomerPulsatingIndicator = createAsyncThunk(
  'client/hideCustomerPulsatingIndicator',
  async (stageInfo: CustomerHideIndicatorModel) => {
    const { customerId } = stageInfo;
    await backofficeApiClient.hideCustomerPulsatingIndicator(stageInfo);

    return backofficeApiClient.getCustomerInfo(customerId as string);
  },
);

interface CreateCommentForCustomerModel {
  entityId: string;
  text: string;
  commentId?: string;
}

const createCommentForCustomer = createAsyncThunk(
  'client/createCommentForCustomer',
  async ({ entityId, text }: CreateCommentForCustomerModel) => {
    return adminApiClient.createNewCustomerComment({
      customerId: entityId,
      text,
    });
  },
);

const updateCommentForCustomer = createAsyncThunk(
  'client/updateCommentForCustomer',
  async ({ commentId, text }: CreateCommentForCustomerModel) => {
    return adminApiClient.updateCustomerComment({
      id: commentId,
      text,
    });
  },
);

interface CommentsForCustomerModel {
  entityId: string;
  pageNumber: number;
  requestId: number;
}

const getCommentsForCustomer = createAsyncThunk(
  'client/getCommentsForCustomer',
  async ({ entityId, pageNumber }: CommentsForCustomerModel) => {
    return adminApiClient.getCustomerComments(pageNumber, entityId);
  },
);

const getEditInfoForCustomerByAdmin = createAsyncThunk(
  'client/getEditInfoForCustomerByAdmin',
  async (customerId: string) => {
    return adminApiClient.getCustomerEditModel(customerId);
  },
);

const getEditDraftInfoForCustomer = createAsyncThunk(
  'client/getEditDraftInfoForCustomer',
  async (customerId: string) => {
    return backofficeApiClient.getCustomerDraftEdit(customerId);
  },
);

const getEditInfoForCustomer = createAsyncThunk(
  'client/getEditInfoForCustomer',
  async (customerId: string) => {
    return backofficeApiClient.getCustomerEditModel(customerId);
  },
);

const getEditAdditionalInfoForCustomer = createAsyncThunk(
  'client/getEditAdditionalInfoForCustomer',
  async (customerId: string) => {
    return adminApiClient.getCustomerEditAdditionalInfo(customerId);
  },
);

const getClientAdditionalInfo = createAsyncThunk(
  'client/getClientAdditionalInfo',
  async (customerId: string) => {
    return backofficeApiClient.getCustomerAdditionalInfo(customerId);
  },
);

const getCertificateFileInfoForCustomer = createAsyncThunk(
  'client/getCertificateFileInfoForCustomer',
  async (customerId: string) => {
    return adminApiClient.getCustomerCertificateOfInsuranceCreatedFile(
      customerId,
    );
  },
);

const hasCustomers = createAsyncThunk('client/hasCustomers', async () => {
  return backofficeApiClient.isExistsMyCustomers();
});

export interface AgreementFileParams {
  fileKey?: string;
  fileName?: string;
  isAdminRole?: boolean;
}

export interface CustomerAgreementFileParams extends AgreementFileParams {
  customerId: string;
}

const getServiceAgreementFileInfoForCustomer = createAsyncThunk(
  'client/getServiceAgreementFileInfoForCustomer',
  async ({
    customerId,
    isAdminRole,
    fileKey,
    fileName,
  }: CustomerAgreementFileParams) => {
    return isAdminRole
      ? adminApiClient.getCustomerAgreementDetailsFile(
          customerId,
          fileKey,
          fileName,
        )
      : backofficeApiClient.getCustomerAgreementDetailsFile(
          customerId,
          fileKey,
          fileName,
        );
  },
);

type CustomerInfoWithoutFileDetails = Omit<
  CustomerAgreementFileParams,
  'fileKey' | 'fileName'
>;

const getCustomerTempToPermConversionFileInfoForCustomer = createAsyncThunk(
  'client/getCustomerTempToPermConversionFileInfoForCustomer',
  async ({ customerId, isAdminRole }: CustomerInfoWithoutFileDetails) => {
    return isAdminRole
      ? adminApiClient.getCustomerTempToPermConversionFile(customerId)
      : backofficeApiClient.getCustomerTempToPermConversionFile(customerId);
  },
);

const getCustomerCertificateOfInsuranceCreatedFile = createAsyncThunk(
  'client/getCustomerCertificateOfInsuranceCreatedFile',
  async ({ customerId, isAdminRole }: CustomerInfoWithoutFileDetails) => {
    return isAdminRole
      ? adminApiClient.getCustomerCertificateOfInsuranceCreatedFile(customerId)
      : backofficeApiClient.getCustomerCertificateOfInsuranceCreatedFileForMember(
          customerId,
        );
  },
);

const getCustomerPurchaseOrderFile = createAsyncThunk(
  'client/getCustomerPurchaseOrderFile',
  async (customerId: string) => {
    return adminApiClient.getCustomerPurchaseOrderFile(customerId);
  },
);

const getCustomersDetailExcel = createAsyncThunk(
  'client/getCustomersDetailExcel',
  async ({ customerId, isAdminRole }: CustomerInfoWithoutFileDetails) => {
    const result = isAdminRole
      ? await adminApiClient.getCustomersDetailExcel(customerId)
      : await backofficeApiClient.getCustomersDetailExcel(customerId);

    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const getSOWTemplateFile = createAsyncThunk(
  'client/getSOWTemplateFile',
  async ({ customerId, isAdminRole }: CustomerInfoWithoutFileDetails) => {
    return isAdminRole
      ? await adminApiClient.getSOWTemplateFile(customerId)
      : await backofficeApiClient.getSOWTemplateFile(customerId);
  },
);

interface GetOnboardingFileInfoForCustomerModel {
  customerId: string;
  fileKey: string;
  isAdminRole?: boolean;
}

const getCustomerOnboardingDocumentationFile = createAsyncThunk(
  'client/getCustomerOnboardingDocumentationFile',
  async ({
    customerId,
    fileKey,
    isAdminRole,
  }: GetOnboardingFileInfoForCustomerModel) => {
    return isAdminRole
      ? await adminApiClient.getCustomerOnboardingDocumentationFile(
          customerId,
          fileKey,
        )
      : await backofficeApiClient.getCustomerOnboardingDocumentationFile(
          customerId,
          fileKey,
        );
  },
);

interface HistoryForCustomerModel {
  entityId: string;
  pageNumber: number;
}
const getHistoryDataForCustomer = createAsyncThunk(
  'client/getHistoryDataForCustomer',
  async ({ entityId, pageNumber }: HistoryForCustomerModel) => {
    return adminApiClient.getCustomerHistory(entityId, 20, pageNumber);
  },
);

const preUploadBulkImportCustomers = createAsyncThunk(
  'client/preUploadBulkImportCustomers',
  async (file: any, { rejectWithValue }) => {
    try {
      return await adminApiClient.importCustomerListFromPlainTable(file);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

interface CreateCustomersModel {
  memberId: string;
  models: CreateCustomerRequest[];
}
const createCustomers = createAsyncThunk(
  'client/createCustomers',
  async (data: CreateCustomersModel) => {
    return adminApiClient.createCustomers(data);
  },
);

const getClientBulkImportTemplate = createAsyncThunk(
  'client/getClientBulkImportTemplate',
  async () => {
    return await adminApiClient.getTemplateFile(
      FileTypeEnum.CustomerCreationTemplate,
    );
  },
);

interface HistoryFileRequestModel {
  customerId?: string;
  fileKey?: string;
  documentType?: CustomerDocumentTypeEnum;
}
const getCustomerHistoryFile = createAsyncThunk(
  'client/getCustomerHistoryFile',
  async ({ customerId, fileKey, documentType }: HistoryFileRequestModel) => {
    return adminApiClient.getCustomerHistoryFileByKey(
      customerId,
      fileKey,
      documentType,
      undefined,
    );
  },
);

const getCustomerGroups = createAsyncThunk(
  'client/getCustomerGroups',
  async () => {
    return adminApiClient.getCustomersGroupsList();
  },
);

const createCustomerGroup = createAsyncThunk(
  'client/createCustomerGroup',
  async (data: CreateCustomerGroupViewModel) => {
    return adminApiClient.createCustomerGroup(data);
  },
);

const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    handleClientModalOpen: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isClientModalOpen: action.payload,
    }),
    handleResetClientInfoByKey: (state) => ({
      ...state,
      clientDetails: undefined,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      Object.assign(state, initialState);
    });
    builder.addCase(getMyApprovedClients.fulfilled, (state, action) => {
      state.approvedClients = action.payload;
    });
    builder.addCase(createNewClient.fulfilled, (state) => {
      state.clientCreatedAt = Date.now();
    });
    builder.addCase(createDraftClient.fulfilled, (state) => {
      state.clientDraftCreatedAt = Date.now();
    });
    builder.addCase(getClientInfoByKey.pending, (state) => {
      state.clientDetails = undefined;
    });
    builder.addCase(getClientInfoByKey.fulfilled, (state, action) => {
      state.clientDetails = action.payload;
    });
    builder.addCase(getClientInfoByKey.rejected, (state) => {
      state.clientDetails = undefined;
    });
    builder.addCase(getCustomerGroups.pending, (state) => {
      state.customerGroups = [];
    });
    builder.addCase(getCustomerGroups.fulfilled, (state, action) => {
      state.customerGroups = action.payload;
    });
    builder.addCase(getCustomerGroups.rejected, (state) => {
      state.customerGroups = [];
    });
    builder.addMatcher(
      isAnyActionOf(
        // Reload history section on customer's sections updates
        updateClient.fulfilled,
        updateClientAdditionalInfo.fulfilled,
        completeClientProgressStep.fulfilled,
      ),
      (state) => {
        state.customerDetailsUpdatedAtByAdmin = Date.now();
      },
    );
  },
});

const { reducer, actions } = clientSlice;
const { handleClientModalOpen, handleResetClientInfoByKey } = actions;

export {
  createNewClient,
  createDraftClient,
  updateDraftClient,
  crateClientFromDraft,
  updateClient,
  updateClientAdditionalInfo,
  getClientRequests,
  getClientInfo,
  getClientInfoByKey,
  getMyApprovedClients,
  getApprovedClientsByMember,
  getMyApprovedClientInfo,
  completeClientProgressStep,
  getApprovedClientsCompanies,
  createCommentForCustomer,
  getCommentsForCustomer,
  getEditInfoForCustomer,
  getEditAdditionalInfoForCustomer,
  getClientAdditionalInfo,
  getServiceAgreementFileInfoForCustomer,
  getCertificateFileInfoForCustomer,
  hasCustomers,
  handleClientModalOpen,
  sendCustomerAdditionalInfoResponse,
  getCustomerTempToPermConversionFileInfoForCustomer,
  getCustomerCertificateOfInsuranceCreatedFile,
  getCustomersDetailExcel,
  getSOWTemplateFile,
  getCustomerHistoryFile,
  getHistoryDataForCustomer,
  hideCustomerPulsatingIndicator,
  getCustomerOnboardingDocumentationFile,
  getEditDraftInfoForCustomer,
  getEditInfoForCustomerByAdmin,
  handleResetClientInfoByKey,
  preUploadBulkImportCustomers,
  getClientBulkImportTemplate,
  createCustomers,
  getAllCustomersCompanies,
  getCustomerPurchaseOrderFile,
  updateCommentForCustomer,
  getCustomerGroups,
  createCustomerGroup,
  getCustomerListExcel,
};
export default reducer;
